import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAlert } from '@/lib/alerts/hooks/useAlert';
import { AccessTokenResponse, useAuth } from '@/lib/auth/hooks/useAuth';
import { Button } from '@heroui/react';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { useGet } from '@/machinery/useGet';

export const Route = createFileRoute('/auth/redirect')({
    component: RouteComponent,
    validateSearch: z.object({
        token: z.string(),
        redirect: z.string().optional(),
    }),
});

function RouteComponent() {
    const { token, redirect } = Route.useSearch();
    const { set } = useAuth();
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    const { data, error, isLoading } = useGet<AccessTokenResponse>({
        request: { url: `/auth/token`, params: { token } },
        enabled: !!token,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchInterval: false,
    });
    const [linkOutOfDate, setLinkOutOfDate] = useState(false);
    useEffect(() => {
        if (error && !linkOutOfDate) {
            setLinkOutOfDate(true);
        }
    }, [error, showAlert, linkOutOfDate]);

    useEffect(() => {
        if (!isLoading && data?.access_token) {
            set(data?.access_token);
            navigate({ to: redirect ?? '/' });
        }
    }, [data?.access_token, isLoading, redirect, navigate, set]);

    return (
        <div className='my-1 max-w-xl md:mx-auto'>
            {linkOutOfDate && (
                <div className='simple-card grid gap-2'>
                    <div className='text-lg font-semibold'>
                        Your link is out of date! Please try logging in or resetting your password.
                    </div>
                    <Link to='/auth/login'>
                        <Button color='primary'>Log in</Button>
                    </Link>
                    <Link to='/auth/forgot-password'>
                        <Button variant='light' color='primary'>
                            Reset password
                        </Button>
                    </Link>
                </div>
            )}
        </div>
    );
}
